import { IconOffice3Outline18 as BuildingIcon } from "nucleo-ui-outline-18"
import Balancer from "react-wrap-balancer"
import { z } from "zod"

import { commonEmailProviders } from "@productlane/lib/src/browser"
import {
	Button,
	Divider,
	Form,
	FormInput,
	FormSwitch,
	useZodForm,
} from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { useSession } from "@/lib/session"

const CreateWorkspaceSchema = z.object({
	name: z.string().min(1),
	enableDomainAccess: z.boolean().optional(),
})

interface IProps {
	onCreated: () => void
	displayHeader?: boolean
}

export function CreateWorkspaceTab({ onCreated, displayHeader }: IProps) {
	const ctx = api.useUtils()

	const { session } = useSession()

	const domain = session?.email.split("@")[1]
	const isWorkEmail =
		!!domain && !commonEmailProviders.includes(domain.toLowerCase())

	const colleagueWorkspace = api.user.colleagueWorkspace.useQuery(undefined, {
		enabled: isWorkEmail,
	})
	const canClaimDomain = isWorkEmail && colleagueWorkspace.data === false

	const workspaceCreateMutation = api.workspace.create.useMutation({
		onSuccess: async () => {
			await ctx.invalidate()
			await ctx.user.getMembership.invalidate()
			await ctx.user.get.invalidate()
			await ctx.user.info.invalidate()
			await ctx.workspace.info.invalidate() // Important for first load after onboarding to display correct data
			await ctx.workspace.slackSettings.invalidate() // Important for slack integration
			await ctx.workspace.slackConnectSettings.invalidate()
			onCreated()
		},
		onError: (error) => {
			captureError(error, { domain: "general", action: "create workspace" })
			toast.error({
				title: "Couldn't create workspace.",
				description: error.message,
			})
		},
	})

	const form = useZodForm({
		schema: CreateWorkspaceSchema,
		defaultValues: { name: "", enableDomainAccess: isWorkEmail },
	})

	return (
		<div className="flex w-full flex-col items-center">
			{displayHeader && (
				<div className="mb-2 w-full text-left text-3xl font-bold">
					Create your workspace
				</div>
			)}
			<Balancer className="mr-auto w-full text-sm text-secondary">
				{displayHeader
					? "Manage customer support with your team."
					: "Create a new workspace to manage support with your team."}
			</Balancer>
			<div className="mt-8 flex w-full items-center justify-center gap-10">
				<Form
					className="w-full space-y-8"
					id="create-workspace-form"
					form={form}
					onSubmit={(values) => {
						workspaceCreateMutation.mutate({
							...values,
							enableDomainAccess: canClaimDomain && values.enableDomainAccess,
						})
					}}
				>
					<div className="max-w-xx mx-auto">
						<FormInput
							showErrorMessage={false}
							icon={BuildingIcon}
							label={!displayHeader ? "Workspace name" : undefined}
							placeholder="Acme"
							name="name"
							type="text"
							inputClassName="h-10 w-full"
							autoFocus
						/>
					</div>

					{canClaimDomain && (
						<>
							<Divider className="" />
							<div>
								<FormSwitch
									className=""
									name="enableDomainAccess"
									label={<div className="text-primary">Domain access</div>}
								/>
								<div className="mt-0.5 text-xs leading-5 text-secondary">
									Anyone with an email ending in{" "}
									<span className="font-medium text-primary">{domain}</span> can
									join.
								</div>
							</div>
						</>
					)}

					<div className="flex w-full justify-center">
						<Button
							form="create-workspace-form"
							type="submit"
							size="xl"
							className="max-w-xss w-full"
							isLoading={workspaceCreateMutation.isLoading}
						>
							Create workspace
						</Button>
					</div>
				</Form>
			</div>
		</div>
	)
}
